import React from 'react';
import { connect } from 'react-redux';
import { DateTime } from 'luxon';

import withStyles from '@mui/styles/withStyles';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';

import activityPanelStyles from '../assets/jss/crm/components/activityPanelStyles';

import { addOutreaches } from '../store/slices/outreachListsSlice';
import { addOutreachesToFirmContact } from '../store/slices/outreachesSlice';
import { addMissive } from '../store/slices/missivesSlice';
import { maybeUpdatePersonLatestOutreachInfo } from '../store/slices/personsSlice';
import { clearTaskAccomplishmentInfo } from '../store/slices/dashboardStatsSlice';

import { createStdRequest, apiFetch, createTwilioRequest, showAlert } from '../utils/utils';

import DA from '../store/ReduxAccess';
import ReduxLoader from '../store/ReduxLoader';

import CrmButton from './CrmButton';
import CrmTextField from './CrmTextField';
import AlertModal from './AlertModal';

const textSendingPanelStyles = (theme) => ({
  ...activityPanelStyles(theme),
  sms_toDiv: {
    '&::before': {
      content: "'To '",
      paddingLeft: '12px',
    },
  },
});

class TextSendingPanel extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      summary: '',
      message: '',
      receiverId: 0,
    };
  }

  componentDidMount() {
    this.setReceiverIdFromProps(this.props);
  }

  componentDidUpdate(prevProps) {
    if (this.props.contacts !== prevProps.contacts) {
      this.setReceiverIdFromProps(this.props);
    }
  }

  setReceiverIdFromProps(props) {
    const contactsWithCellPhones = this.getContactsWithCellPhones(props);
    if (contactsWithCellPhones.length === 0) {
      this.setState({ receiverId: 0 });
    } else {
      this.setState({ receiverId: contactsWithCellPhones[0].id });
    }
  }

  handleMessageChange(newVal) {
    this.setState({ message: newVal });
  }

  handleMessageReceiverChange(event) {
    this.setState({ receiverId: event.target.value });
  }

  cancelText() {
    this.setState({ message: '' });
  }

  buildAllevaApiStruct(firmId, senderId, receiverId) {
    /*
{
    public int OutreachId { get; set; }
    public string Date { get; set; }
    public int FirmId { get; set; }
    public int SenderId { get; set; }
    public int ReceiverId { get; set; }
    public int DlId { get; set; }
    public int MissiveId { get; set; }
    public MissiveApiStruct Missive { get; set; }
        public int MissiveId { get; set; }
        public int FirmId { get; set; }
        public int CreatorId { get; set; }
        public string TypeStr { get; set; }
        public string Name { get; set; }
        public string Description { get; set; }
        public string Content { get; set; }
}
 */
    const content = this.state.message.trim();
    const summary = content.length >= 42 ? content.substring(0, 41) + '...' : content;
    return {
      outreachId: 0,
      date: DateTime.local().toISO(),
      firmId: firmId,
      senderId: senderId,
      receiverId: receiverId,
      dlId: 0,
      missiveId: 0,
      missive: {
        missiveId: 0,
        firmId: firmId,
        creatorId: senderId,
        typeStr: 'PersonalSMS',
        name: summary,
        description: summary,
        content,
      },
    };
  }

  kimsPhoneNumber = '+16503872696';
  stevesPhoneNumber = '+12063319150';

  buildTwilioApiStruct() {
    // const Tos = (window.location.hostname === "localhost") ? [this.kimsPhoneNumber] : [this.stevesPhoneNumber];
    const Tos = window.location.hostname === 'localhost' ? [this.kimsPhoneNumber] : [this.getCellPhoneForReceiver()];
    return {
      Tos,
      Message: this.state.message.trim(),
    };
  }

  sendText() {
    this.setState({ errorMessage: '' });
    const firmId = DA.getLoggedInFirmInfo().firmId;
    const senderId = DA.getLoggedInUserInfo().personId;
    const twilioApiStruct = this.buildTwilioApiStruct();
    if (!twilioApiStruct.Tos[0]) {
      showAlert(this, 'Unable to find mobile number for receiver');
      return;
    }
    const twilioRequest = createTwilioRequest(firmId, twilioApiStruct);

    const actionOnGoodTwilioApiCallResponse = (json) => {
      const allevaUri = `/api/firm/${firmId}/outreach`;
      const receiverId = this.state.receiverId;
      const allevaApiStruct = this.buildAllevaApiStruct(firmId, senderId, receiverId);
      // delayed until now because the content was needed by buildAllevaApiStruct()
      this.setState({ summary: '', message: '' });
      const allevaRequest = createStdRequest(allevaUri, 'post', allevaApiStruct);
      const actionOnGoodAllevaCallResponse = (json) => {
        console.log('SMS successfully sent');
        // need to do something to affect the display; something like:
        // this.props.addOutreach(allevaApiStruct);
        this.props.clearTaskAccomplishmentInfo();
        const outreachStruct = json;
        this.props.addMissive(outreachStruct.missive);
        const reduxStruct = ReduxLoader.convertOutreachStructIntoReduxStruct(outreachStruct);
        const shouldStoreAsFirmContactOutreach = !!this.props.activityColumn;
        if (shouldStoreAsFirmContactOutreach) {
          this.props.addOutreachesToFirmContact({ contactId: receiverId, outreaches: [reduxStruct] });
        } else {
          this.props.addOutreaches({ personId: receiverId, outreaches: [reduxStruct] });
        }
        if (this.props.activityColumn) {
          const visibleFirmId = this.props.visibleFirmId;
          this.props.activityColumn.buildActivityGroupsFromReduxStore(visibleFirmId);
        } else {
          const partyId = this.props.client ? this.props.client.personId : receiverId;
          this.props.page.buildActivityGroupsFromReduxStore(partyId);
        }
        this.props.maybeUpdatePersonLatestOutreachInfo({
          personId: receiverId,
          date: outreachStruct.date,
          description: outreachStruct.missive.description,
        });
      };
      // The following call creates the Outreach and Missive records in the DB
      apiFetch(this, allevaRequest, actionOnGoodAllevaCallResponse);
    }; // This is the end of the definition of actionOnGoodTwilioApiCallResponse

    // The following call actually sends the SMS message
    apiFetch(this, twilioRequest, actionOnGoodTwilioApiCallResponse);
  }

  getContactsWithCellPhones(props) {
    const contacts = props.contacts;
    if (!contacts) {
      return [];
    }
    const filtered = contacts.filter((contact) => this.hasCellPhone(contact));
    return filtered.map((contact) => {
      return { id: contact.personId, name: contact.preferredFullName };
    });
  }

  hasCellPhone(contact) {
    const phoneNumbers = contact.phoneNumbers;
    return phoneNumbers.some((phone) => phone.roles.indexOf('Mobile') >= 0);
  }

  getCellPhoneForReceiver() {
    const receiverId = this.state.receiverId;
    const receiver = this.props.contacts.find((person) => person.personId === receiverId);
    if (!receiver) {
      showAlert(this, 'Not able to find receiver ' + receiverId + ' in list of contacts');
      return '';
    }
    const phoneNumbers = receiver.phoneNumbers;
    const mobile = phoneNumbers.find((phone) => phone.roles.indexOf('Mobile') >= 0);
    return mobile ? mobile.number : '';
  }

  render() {
    const { classes } = this.props;
    const contactsWithCellPhones = this.getContactsWithCellPhones(this.props);
    return (
      <div>
        <AlertModal show={this.state.shouldShowAlertModal} renderer={this} text={this.state.alertModalText} />

        <div className={classes.sms_toDiv}>
          {contactsWithCellPhones.length === 1 ? (
            <span>{contactsWithCellPhones[0].name}</span>
          ) : (
            <FormControl variant="standard">
              <Select
                variant="standard"
                value={this.state.receiverId}
                onChange={(event) => this.handleMessageReceiverChange(event)}
                inputProps={{
                  name: 'receiverId',
                  id: 'receiver-id',
                }}
              >
                <MenuItem value="0">
                  <em>Pick One</em>
                </MenuItem>
                {contactsWithCellPhones.map((contact) => (
                  <MenuItem key={contact.id} value={contact.id}>
                    {contact.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </div>
        <div className={classes.activityPanel_content}>
          <CrmTextField
            label="Type text message here"
            fullWidth
            multiline
            value={this.state.message}
            onChange={(event) => this.handleMessageChange(event.target.value)}
          />
        </div>
        {this.state.message.trim().length > 0 && (
          <div className={classes.u_alignRight}>
            <CrmButton outlined onClick={() => this.cancelText()}>
              Cancel
            </CrmButton>
            <CrmButton
              disabled={this.state.receiverId === 0 || this.state.receiverId === '0'}
              onClick={() => this.sendText()}
            >
              Send
            </CrmButton>
          </div>
        )}
      </div>
    );
  }
}

const actionProducers = {
  addOutreaches,
  addOutreachesToFirmContact,
  addMissive,
  maybeUpdatePersonLatestOutreachInfo,
  clearTaskAccomplishmentInfo,
};

export default connect(null, actionProducers)(withStyles(textSendingPanelStyles)(TextSendingPanel));
